import { ORDContragentRole } from '@synapex/prisma/client'
import { WorkspaceType } from '@synapex/shared2/entities'

import {
    WorkspaceDescriptor,
    WorkspaceWithLinkDescriptor,
} from '@synapex/shared2/entities'
import { WithOptional } from '@synapex/tools/typescript'
import { LinksQueryKeyConstants } from './invite-links-group/invite-links-group.query.constants'
import { linkQueryKey, workspaceQueryKey } from './utils'

type QueryMutationKey =
    | ['advertiser-account']
    | ['advertiser-account', number, 'users']
    | ['advertiser-account', 'workspace', 'site' | 'channel', number, 'users']
    | ['channel', number]
    | ['site', number]

declare module '@tanstack/react-query' {
    interface Register {
        queryKey:
            | [
                  'tme-info',
                  {
                      username?: string | null
                      invite_hash?: string | null
                  },
              ]
            | ReturnType<(typeof apiQueryKey)[keyof typeof apiQueryKey]>
            | [
                  (typeof LinksQueryKeyConstants)[keyof typeof LinksQueryKeyConstants],
              ]
            | readonly [
                  (typeof LinksQueryKeyConstants)[keyof typeof LinksQueryKeyConstants],
                  number,
              ]
            | readonly ['creatives', WorkspaceType, number]
            | readonly ['yandexGoals', number, number]
            | readonly ['yandexProfile', number]
            | readonly ['yandexCounters', number]
            | readonly ['channelPost', number, string, number]
            | readonly ['sitePost', number, string, number]
            | readonly ['kktu']
            | readonly ['banks', string]
            | readonly ['banks', 'search', string | undefined]
            | readonly ['siteUserIcon', string]
            | readonly ['feature-flags']
            | readonly ['audienceData', number]
            | readonly ['advertiser']
            | readonly ['channel-request', string]
            | readonly ['referral']
            | QueryMutationKey

        mutationKey:
            | QueryMutationKey
            | ['channel-request']
            | ['advertiser-account', number]
            | ['advertiser-account', number, 'users', number]
            | [
                  'advertiser-account',
                  'workspace',
                  'site' | 'channel',
                  number,
                  'users',
                  number,
              ]
            | [`mutation-${string}`]
            | ['generate-contract']
            | ['update-contract']
            | ['delete-contract']
            | ['upload-file']
            | ['generate-act']
            | ['delete-act']
            | ['update-act-number']
            | ['update-contract-number']
    }
}

export const apiQueryKey = {
    //per workspace
    additionalOrdData: (..._: unknown[]) => ['advertiser-account'],

    contragents: (
        wd: WorkspaceDescriptor,
        roles: readonly ORDContragentRole[]
    ) => {
        const record = roles.reduce<Partial<Record<ORDContragentRole, true>>>(
            (result, role) => ({ ...result, [role]: true }),
            {}
        )

        return workspaceQueryKey('contragents', wd, [record])
    },

    vkOrdCreatives: (
        wwld: WorkspaceWithLinkDescriptor,
        creativeId?: number | null
    ) => linkQueryKey('vk-ord-creatives', wwld, creativeId ? [creativeId] : []),

    checkInn: (wd: WorkspaceDescriptor, inn?: string | null) =>
        workspaceQueryKey('/ord/check-inn', wd, inn ? [inn] : []),

    contracts: (
        wd: WorkspaceDescriptor,
        contragentDistributorId?: number | null
    ) =>
        workspaceQueryKey(
            'contracts',
            wd,
            contragentDistributorId ? [contragentDistributorId] : []
        ),

    invoices: (
        wd: WorkspaceDescriptor,
        contragentDistributorId?: number | null
    ) =>
        workspaceQueryKey(
            'invoices',
            wd,
            contragentDistributorId ? [contragentDistributorId] : []
        ),

    acts: (wd: WorkspaceDescriptor, contragentDistributorId?: number | null) =>
        workspaceQueryKey(
            'acts',
            wd,
            contragentDistributorId ? [contragentDistributorId] : []
        ),

    linkedLinksByContract: (
        wd: WorkspaceDescriptor,
        type: 'draft' | 'custom',
        documentId?: number | null
    ) =>
        workspaceQueryKey('linkedLinksByContract', wd, [
            type,
            documentId ? [documentId] : [],
        ]),

    googleUserInfo: (..._: unknown[]) => ['advertiser-account'],

    agencyContracts: (wd?: WorkspaceDescriptor) =>
        workspaceQueryKey('agencyContracts', wd),

    //per link
    markingFormData: (
        wwld?: WithOptional<WorkspaceWithLinkDescriptor, 'link_id'>
    ) => linkQueryKey('marking-form-data', wwld),

    vkOrdHistory: (
        wwld?: WithOptional<WorkspaceWithLinkDescriptor, 'link_id'>
    ) => linkQueryKey('vk-ord-history', wwld),

    creativePreview: (input: WorkspaceWithLinkDescriptor) =>
        linkQueryKey('creative-preview', input),

    //
    agencyCreatives: (agencyContractId?: number) => {
        if (agencyContractId) {
            return ['agency-creatives', agencyContractId] as const
        }
        return ['agency-creatives'] as const
    },
}
